import { Box, Chip, Stack, Switch, Typography } from "@mui/material";
import {
  ExplorerSource,
  FilterResponse,
  FilterOption,
  Value,
} from "../../../models/Explorer";
import {
  ExplorerEnvironment,
  useExplorerStore,
} from "../../../data/ExplorerStore";
import { FeatureFlags } from "../../../util/featureFlags";

/** Dev source picker choices: season defaults, or one provider for every season. */
const SOURCE_CHOICES: { value: ExplorerSource | null; label: string }[] = [
  { value: null, label: "By season" },
  { value: "ss", label: "SS" },
  { value: "ctg", label: "CTG" },
];

/**
 * Dev builds only (DESIGN §11 q3: the product never offers a provider choice).
 * Sets element 0's `source`; "By season" clears it so each season reads its
 * default provider.
 */
function SourcePicker() {
  const source = useExplorerStore(
    ({ metricGroups }) => metricGroups[0].source ?? null,
  );
  const environment = useExplorerStore(({ environment }) => environment);
  const setQuerySource = useExplorerStore(
    ({ setQuerySource }) => setQuerySource,
  );
  if (!FeatureFlags.sourcePicker || environment !== ExplorerEnvironment.NBA)
    return null;
  return (
    <Box data-testid="source-picker">
      <Typography variant="caption" sx={sectionLabelSx}>
        Source (dev)
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {SOURCE_CHOICES.map((choice) => {
          const checked = source === choice.value;
          return (
            <Chip
              key={choice.label}
              label={choice.label}
              variant={checked ? "filled" : "outlined"}
              onClick={() => setQuerySource(choice.value)}
              sx={chipSx(checked)}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

/**
 * Season types in reading order, by core value and by the SS / SkillCorner
 * label a saved query may hold. The Cup final is its own type, in neither the
 * regular season nor the playoffs (decision CA′). Unlisted types follow,
 * alphabetically.
 */
export const SEASON_TYPE_ORDER = [
  "regular",
  "regular season",
  "playoffs",
  "playoff",
  "playin",
  "cup_final",
  "preseason",
  "allstar",
];

export interface SeasonTypeChip {
  value: string;
  label: string;
  checked: boolean;
}

/**
 * A selected value belongs to a chip by its value, or by the chip's label: the
 * nba picker offers core values (`regular`, labelled "regular season") while
 * saved queries and the draft picker hold the label itself.
 */
export function selectsOption(selected: Value, option: Value): boolean {
  return (
    selected.value === option.value ||
    selected.value === option.label ||
    selected.label === option.label
  );
}

/**
 * The Season Type chips: the picker's options in reading order, then any
 * selected value the picker no longer offers (a saved `playoff`), checked, so
 * the query never filters on something the builder hides.
 */
export function seasonTypeChips(
  options: Value[] | undefined,
  selected: Value[],
): SeasonTypeChip[] {
  const rank = (v: Value) => {
    const i = SEASON_TYPE_ORDER.indexOf(v.value);
    return i === -1 ? SEASON_TYPE_ORDER.length : i;
  };
  const offered = (options ?? [])
    .slice()
    .sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label))
    .map((o) => ({
      value: o.value,
      label: o.label,
      checked: selected.some((v) => selectsOption(v, o)),
    }));
  const orphans = selected
    .filter((v) => !(options ?? []).some((o) => selectsOption(v, o)))
    .map((v) => ({ value: v.value, label: v.label || v.value, checked: true }));
  return [...offered, ...orphans];
}

const sectionLabelSx = {
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontSize: "0.75rem",
  color: "rgba(0, 0, 0, 0.6)",
  display: "block",
  mb: 0.5,
} as const;

const chipSx = (checked: boolean) => ({
  borderRadius: "3px",
  fontSize: "0.8rem",
  height: "28px",
  fontWeight: checked ? 700 : 400,
  backgroundColor: checked ? "#4c4c4c" : "transparent",
  color: checked ? "#fff" : "inherit",
  borderColor: "#bdbdbd",
  "&:hover": {
    backgroundColor: checked ? "#3a3a3a" : "#eeeeee",
  },
}) as const;

export default function DefaultQueryGroupFilters() {
  const {
    seasonFilterIndex,
    seasonFilter,
    seasonFilterOption,
    seasonTypeFilterIndex,
    seasonTypeFilter,
    seasonTypeFilterOption,
    garbageFilterIndex,
    garbageFilter,
    updateDimension,
  } = useExplorerStore(({ metricGroups, filters, updateDimension }) => {
    const queryFilters = metricGroups[0].filters;
    const seasonFilterIndex = queryFilters.findIndex(
      (f) => f.name?.value === "Season"
    );
    const seasonTypeFilterIndex = queryFilters.findIndex(
      (f) => f.name?.value === "SeasonType"
    );
    const garbageFilterIndex = queryFilters.findIndex(
      (f) => f.name?.value === "Garbage"
    );
    const seasonFilter = queryFilters[seasonFilterIndex];
    const seasonTypeFilter = queryFilters[seasonTypeFilterIndex];
    const garbageFilter = queryFilters[garbageFilterIndex];
    return {
      seasonFilterIndex,
      seasonFilter,
      seasonFilterOption: getFilterOption(filters, "Season"),
      seasonTypeFilterIndex,
      seasonTypeFilter,
      seasonTypeFilterOption: getFilterOption(filters, "SeasonType"),
      garbageFilterIndex,
      garbageFilter,
      updateDimension,
    };
  });

  const isGarbageFiltered = ((): boolean => {
    if (
      !garbageFilter?.values ||
      typeof garbageFilter?.values === "number" ||
      typeof garbageFilter?.values === "string"
    )
      return false;
    return !garbageFilter.values.some((v) => v.value === "True");
  })();

  function getFilterOption(
    filters: FilterResponse | null,
    filterName: string | undefined
  ): FilterOption | undefined {
    if (!filters || !filterName) return undefined;
    return filters[filterName];
  }

  function chipToggle(chip: Value, checkBoxType: "season" | "seasonType") {
    const [filter, filterIndex] =
      checkBoxType === "season"
        ? [seasonFilter, seasonFilterIndex]
        : [seasonTypeFilter, seasonTypeFilterIndex];
    const filterForUpdate = Object.assign({}, filter);
    if (!Array.isArray(filterForUpdate.values)) return;
    const current = filterForUpdate.values;
    const matches = (v: Value) =>
      checkBoxType === "season"
        ? v.value === chip.value
        : selectsOption(v, chip);

    filterForUpdate.values = current.some(matches)
      ? current.filter((v) => !matches(v))
      : [...current, { label: chip.label, value: chip.value }];
    updateDimension(0, "filters", filterIndex, filterForUpdate);
  }

  function toggleChange(_: any, checked: boolean) {
    const filterForUpdate = Object.assign({}, garbageFilter);
    if (!Array.isArray(filterForUpdate.values)) return;
    const filterValuesForUpdate = filterForUpdate.values?.slice() ?? [];

    if (checked) {
      const valueIndex = filterForUpdate.values?.findIndex(
        (v) => v.value === "True"
      );
      if (typeof valueIndex === "number") {
        filterValuesForUpdate.splice(valueIndex, 1);
      }
    } else {
      filterValuesForUpdate.push({ label: "True", value: "True" });
    }
    filterForUpdate.values = filterValuesForUpdate;
    updateDimension(0, "filters", garbageFilterIndex, filterForUpdate);
  }

  return (
    <Stack spacing={2} mb={1.5}>
      <SourcePicker />
      {seasonFilterIndex !== -1 && (
        <Box>
          <Typography variant="caption" sx={sectionLabelSx}>
            Season
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {seasonFilterOption?.values
              .slice()
              .sort((a, b) => (a.label < b.label ? 1 : -1))
              .map((v) => {
                if (!Array.isArray(seasonFilter?.values)) return null;
                const checked = seasonFilter.values.some(
                  (v2) => v.value === v2.value
                );
                return (
                  <Chip
                    key={v.value}
                    label={v.label}
                    variant={checked ? "filled" : "outlined"}
                    onClick={() => chipToggle(v, "season")}
                    sx={chipSx(checked)}
                  />
                );
              })}
          </Stack>
        </Box>
      )}
      {seasonTypeFilterIndex !== -1 && (
        <Box>
          <Typography variant="caption" sx={sectionLabelSx}>
            Season Type
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {Array.isArray(seasonTypeFilter?.values) &&
              seasonTypeChips(
                seasonTypeFilterOption?.values,
                seasonTypeFilter.values,
              ).map((chip) => (
                <Chip
                  key={chip.value}
                  label={chip.label}
                  variant={chip.checked ? "filled" : "outlined"}
                  onClick={() => chipToggle(chip, "seasonType")}
                  sx={chipSx(chip.checked)}
                />
              ))}
          </Stack>
        </Box>
      )}
      {garbageFilterIndex !== -1 && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" sx={sectionLabelSx}>
            Remove garbage time
          </Typography>
          <Switch
            size="small"
            checked={isGarbageFiltered}
            onChange={toggleChange}
          />
        </Stack>
      )}
    </Stack>
  );
}
